import z from 'zod'

import { addCalendarDays, isoDateTime } from '../lib/date'

export const patientSexSchema = z.enum(['male', 'female', 'unknown'])
export type PatientSex = z.infer<typeof patientSexSchema>

// Narrower than file attachments: the photo renders in an <img>, so only browser-displayable formats.
export const PATIENT_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const

export const patientSchema = z.object({
  id: z.uuid(),
  ownerId: z.uuid(),
  name: z.string(),
  species: z.string(),
  breed: z.string().nullable(),
  color: z.string().nullable(),
  /** A day stored at midnight UTC in a timestamp column; read it with `.slice(0, 10)`. */
  dateOfBirth: isoDateTime.nullable(),
  sex: patientSexSchema,
  isNeutered: z.boolean().nullable(),
  microchipNumber: z.string().nullable(),
  stripeSubscriptionId: z.string().nullable(),
  archivedAt: isoDateTime.nullable(),
  archivedReason: z.string().nullable(),
  internalNotes: z.string().nullable(),
  photoFileId: z.uuid().nullable(),
  dentalRequestedAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})

export type Patient = z.output<typeof patientSchema>

/** Format patient sex with neutered/spayed status for display. */
export function formatSex(sex: string, isNeutered: boolean | null): string {
  if (isNeutered === true) return `${sex} (Neutered)`
  if (isNeutered === false) return `${sex} (Intact)`
  return sex
}

/** A date of birth as forms send it; the bounds catch mistyped years such as `0025`. */
export const dateOfBirthSchema = z.iso
  .date()
  .refine((date) => date >= '1950-01-01', 'Date of birth is too far in the past. Check the year')
  // Tomorrow in UTC, so a pet born today is accepted in every time zone.
  .refine(
    (date) => date <= addCalendarDays(new Date().toISOString().slice(0, 10), 1),
    "Date of birth can't be in the future"
  )

export const DOG_BREEDS = [
  'Affenpinscher',
  'Afghan Hound',
  'Africanis',
  'Airedale Terrier',
  'Akbash',
  'Akita',
  'Akita Inu',
  'Alapaha Blue Blood Bulldog',
  'Alaskan Klee Kai',
  'Alaskan Malamute',
  'Alpine Dachsbracke',
  'American Akita',
  'American Bulldog',
  'American Bully',
  'American Cocker Spaniel',
  'American Eskimo',
  'American Foxhound',
  'American Hairless Dog',
  'American Pit Bull Terrier',
  'American Shepherd',
  'American Staffordshire Terrier',
  'American Water Spaniel',
  'Anatolian Shepherd (Kangal)',
  'Appenzeller Sennenhund',
  'Armenian Gampr',
  'Arubian Cunucu',
  'Aspin',
  'Aussiedoodle',
  'Australian Bulldog',
  'Australian Cattle Dog',
  'Australian Cobberdog',
  'Australian Kelpie Sheep Dog',
  'Australian Labradoodle',
  'Australian Shepherd',
  'Australian Terrier',
  'Austrian Pinscher',
  'Auvergne Pointer',
  'Azawakh',
  'Bakharwal Dog',
  'Bandog',
  'Barbet',
  'Basenji',
  'Basque Shepherd Dog',
  'Basset Artesien Normand',
  'Basset Fauve de Bretagne',
  'Basset Griffon Vendeen',
  'Basset Hound',
  'Beagle',
  'Bearded Collie',
  'Beauceron',
  'Bedlington Terrier',
  'Belgian Malinois',
  'Belgian Sheepdog',
  'Belgian Shepherd (Groenendael)',
  'Belgian Shepherd (Malinois)',
  'Belgian Tervuren',
  'Bergamasco',
  'Berger Blanc Suisse',
  'Berger Picard',
  'Bernedoodle',
  'Bernese Mountain Dog',
  'Bichon Bolognese',
  'Bichon Frise',
  'Bichon Havanais',
  'Bichon Maltese',
  'Biewer Yorkshire Terrier',
  'Black and Tan Coonhound',
  'Black Mouth Cur',
  'Black Russian Terrier',
  'Bloodhound',
  'Blue Heeler',
  'Blue Lacy',
  'Blue Picardy Spaniel',
  'Blue Tick Coonhound',
  'Bobtail',
  'Boerboel',
  'Bolonka Zwetna',
  'Border Collie',
  'Border Terrier',
  'Borzoi',
  'Boston Terrier',
  'Bouvier des Flandres',
  'Boxer',
  'Boykin Spaniel',
  'Bracco Italiano',
  'Brac De Weimar',
  'Braque Francais',
  'Briard',
  'Brittany',
  'Brittany Spaniel',
  'Brussels Griffon',
  'Bucovina Shepherd Dog',
  'Bull Arab',
  'BullMastiff',
  'Bull Terrier',
  'Bull Terrier (miniature)',
  'Bully Kutta',
  'Cairn terrier',
  'Canaan',
  'Cane Corso',
  'Canis Panther',
  'Cao da Serra de Aires',
  'Carolina Dog',
  'Carpathian Shepherd Dog',
  'Catahoula Leopard Dog',
  'Catalan Sheepdog',
  'Caucasian Shepherd Dog',
  'Cavachon',
  'Cavalier King Charles Spaniel',
  'Cavapoo',
  'Cavoodle',
  'Celium Terrier',
  'Central Asian Shepherd Dog',
  'Cesky Fousek',
  'Cesky Terrier',
  'Chesapeake Bay Retriever',
  'Chihuahua',
  'Chilean Terrier',
  'Chinese Crested Dog',
  'Chinese Foo Dog',
  'Chinook',
  'Chippiparai',
  'Chiribaya',
  'Chiweenie',
  'Chow-Chow',
  'Cirneco dell Etna',
  'Clumber Spaniel',
  'Coban Kopegi',
  'Cockapoo',
  'Cockapoodle',
  'Cockerlierpoo',
  'Cocker Spaniel',
  'Collie',
  'Colorado Mountain Dog',
  'Continental Toy Spaniel',
  'Coonhound',
  'Coton de Tulear',
  'Cretan Hound',
  'Cross',
  'Curly-Coated Retriever',
  'Czechoslovakian Wolfdog',
  'Dachshund',
  'Dalmatian',
  'Dandie Dinmont Terrier',
  'Danish-Swedish Farmdog',
  'Decker Hunting Terrier',
  'Deerhound',
  'Deutsch Drahthaar',
  'Deutscher Wachtelhund',
  'Dingo',
  'Doberman',
  'Doberman Pinscher',
  'Dogo Argentino',
  'Dogo Canario',
  'Dogue de Bordeaux',
  'Dorgi',
  'Double Doodle',
  'Drentse Patrijshond',
  'Dutch Shepherd',
  'English Bulldog',
  'English Cocker Spaniel',
  'English Foxhound',
  'English Labrador Retriever',
  'English Setter',
  'English Shepherd',
  'English Springer Spaniel',
  'English Toy Spaniel',
  'Entlebuch Shepherd Dog',
  'Eurasian',
  'Euro Mountain Sheparnese',
  'Feist',
  'Field Spaniel',
  'Fila Brasileiro',
  'Finnish Lapphund',
  'Finnish Spitz',
  'Flat-Coated Retriever',
  'Fox Terrier',
  'French Bulldog',
  'French Spaniel',
  'Gaddi Kutta',
  'Galgo Español',
  'Ganaraskan',
  'Gerberian Shepsky',
  'German Longhaired Pointer',
  'German Pinscher',
  'German Pudelpointer',
  'German Shepherd',
  'German Shorthaired Pointer',
  'German Spitz',
  'German Wirehaired Pointer',
  'Giant Schnauzer',
  'Glen of Imaal terrier',
  'Goberian',
  'Golden Dachshund',
  'Golden Doodle',
  'Golden Retriever',
  'Gordon Setter',
  'Great Dane',
  'Greater Swiss Mountain Dog',
  'Great Pyrenees',
  'Greek Harehound',
  'Greek Kokoni',
  'Greek Molossus of Epirus',
  'Greek Sheepdog',
  'Greek White Sheepdog',
  'Greenland Dog',
  'Greyhound',
  'Greyster',
  'Griffon de Bruxelles',
  'Groenendael',
  'Groodle',
  'Gull Dong',
  'Gull Terrier',
  'Hanover Hound',
  'Harrier',
  'Havanese',
  'Heeler',
  'Heideterrier',
  'Hound',
  'Hovaward',
  'Hovawart',
  'Hungarian Agár',
  'Hungarian Vizsla',
  'Huntaway',
  'Huskita',
  'Husky arctic',
  'Husky siberian',
  'Ibizan Hound',
  'Icelandic Sheepdog',
  'Indian Local Breed',
  'Indian Pariah Dog',
  'Indian Spitz',
  'Irish Setter',
  'Irish Terrier',
  'Irish Water Spaniel',
  'Irish Wolfhound',
  'Italian Greyhound',
  'Italian Tzu',
  'Jack Russell Terrier',
  'Jagd Terrier',
  'Jagdterrier (german hunt)',
  'Japanese Chin',
  'Japanese Spitz',
  'Jonangi',
  'Kaikadi',
  'Kanni',
  'Karakachan',
  'Karelian Bear Dog',
  'Keeshond',
  'Kelpie',
  'Kerry Blue Terrier',
  'Kintamani',
  'Kombai',
  'Komondor',
  'Kooikerhondje',
  'Koolie',
  'Korean Jindo Dog',
  'Kumaon Mastiff',
  'Kuvasz',
  'Labernese',
  'Labradoodle',
  'Labrador Retriever',
  'Laeken Ciobanesc belgian',
  'Laika',
  'Lakeland Terrier',
  'Lancashire Heeler',
  'Landseer',
  'Lapphound suedez',
  'Lapphund finlandez',
  'Large Münsterländer',
  'Large Terrier',
  'Leonberger',
  'Lhasa Apso',
  'Liangshan Hound',
  'Limier Bavarez',
  'Limier De Hanovra',
  'Llewellin Setter',
  'Longhaired Dachshund',
  'Louisiana Catahoula',
  'Louisiana Catahoula Leopard Dog',
  'Lowchen',
  'Lundehund',
  'Lurcher',
  'Mahratta Greyhound',
  'Malinois Ciobanesc belgian',
  'Maltese',
  'Maltipoo',
  'Manchester Terrier',
  'Maremmano-Abruzzese Sheepdog',
  'Maremma Sheepdog',
  'Mastiff',
  'Mastino Napoletano',
  'Mauzer Ysterberger',
  'McNab',
  'Medium Poodle',
  'Metis',
  'Micro Bully',
  'Mi-Ki',
  'Miniature American Shepherd',
  'Miniature Australian Shepherd',
  'Miniature Bulldog',
  'Miniature Dachshund',
  'Miniature Golden Doodle',
  'Miniature Pinscher',
  'Miniature Schnauzer',
  'Mini Poodle',
  'Mix',
  'Moodle',
  'Morkie',
  'Moscow Watchdog',
  'Mountain Cur',
  'Mountain Feist',
  'Mudhol Hound',
  'Mudi',
  'Native American Indian',
  'Neapolitan Mastiff',
  'New England Toy Terrier',
  'Newfoundland',
  'New Guinea Singing Dog',
  'Norfolk Terrier',
  'Norrbottenspets',
  'Norwegian Buhund',
  'Norwegian Elkhound',
  'Norwich Terrier',
  'Nova Scotia Duck Tolling Retriever',
  'Ogar de serbia',
  'Old Dominion Terrier',
  'Olde English Bulldog',
  'Old English Sheepdog',
  'Otterhound',
  'Pandikona',
  'Papillon',
  'Parson Russell Terrier',
  'Patterdale Terrier',
  'Pekingese',
  'Pembroke Welsh Corgi',
  'Peruvian Hairless Dog',
  'Petit Basset Griffon Vendeen',
  'Petit Bleu de Gascogne',
  'Pharaoh Hound',
  'Plott Hound',
  'Pocket Bully',
  'Podenco',
  'Pointer',
  'Polish Lowland Sheepdog',
  'Pomapoo',
  'Pom Chi',
  'Pomeranian',
  'Pomsky',
  'Poodle',
  'Poodle Toy',
  'Portuguese Water Dog',
  'Potcake',
  'Presa Canario',
  'Pug',
  'Pugalier',
  'Puggle',
  'Puli',
  'Pumi',
  'Pyrenean Shepherd',
  'Rajapalayam',
  'Rampur Greyhound',
  'Rat Terrier',
  'Redbone Coonhound',
  'Red Heeler',
  'Rhodesian Ridgeback',
  'Ridgeback',
  'Romanian Mioritic Shepherd Dog',
  'Romanian Raven Shepherd Dog',
  'Rottweiler',
  'Russian Spaniel',
  'Russian Toy Terrier',
  'Saint Bernard',
  'Saluki',
  'Samoyed',
  'Sarplaninac Shepherd Dog',
  'Sato',
  'Schanuzer',
  'Schapendoes',
  'Schipperke',
  'Schnoodle',
  'Scottish Deerhound',
  'Scottish Terrier',
  'Sealyham Terrier',
  'Segugio Maremmano',
  'Serbian Hound',
  'Shar Pei',
  'Shetland Sheepdog',
  'Shiba Inu',
  'Shih Tzu',
  'Shikoku',
  'Shiloh Shepherd',
  'Shorkie Poo',
  'Silken Windhound',
  'Silky Terrier',
  'Singapore Special',
  'Skye Terrier',
  'Sloughi',
  'Slovenský kopov',
  'Small Münsterländer',
  'Smooth Fox Terrier',
  'Soft Coated Wheaten Terrier',
  'Southeast Asian Village Dog',
  'South Russian Ovcharka',
  'Spanish Mastiff',
  'Spanish Water Dog',
  'Spinone Italiano',
  'Spitz',
  'Spoodle',
  'Sprocker Spaniel',
  'Stabyhoun',
  'Staffordshire Bull Terrier',
  'Staffy',
  'Staghound',
  'Standard Poodle',
  'Standard Schnauzer',
  'Sussex Spaniel',
  'Swedish Vallhund',
  'Swissy Saint',
  'Taiwanese Mountain Dog',
  'Tchiorny Terrier',
  'Teddy Roosevelt Terrier',
  'Tenterfield Terrier',
  'Terrier Lucas',
  'Terrier Mix',
  'Tervuren',
  'Thai Ridgeback',
  'Tibetan Mastiff',
  'Tibetan Spaniel',
  'Tibetan Terrier',
  'Tornjak',
  'Tosa Inu',
  'Toy Australian Shepherd',
  'Toy Fox Terrier',
  'Toy Manchester Terrier',
  'Transylvanian Hound',
  'Treeing Tennessee Brindle',
  'Treeing Walker Coonhound',
  'Unknown',
  'Valley Bulldog',
  'Vanjari Hound',
  'Vikhan Sheepdog',
  'Volpino Italiano',
  'Weimaraner',
  'Welsh Corgi Cardigan',
  'Welsh Corgi Pembroke',
  'Welsh Springer Spaniel',
  'Welsh Terrier',
  'West highland White Terrier',
  'Wetterhoun',
  'Whippet',
  'White Shepherd',
  'White Swiss Shepherd',
  'Wire-haired Dachshund',
  'Wirehaired Pointing Griffon',
  'Wire Haired Terrier',
  'Wirehaired Vizsla',
  'Xoloitzcuintle',
  'Yorkshire Terrier',
  'Ysterberg Mastiff',
] as const

export const CAT_BREEDS = [
  'Abyssinian',
  'African wild cat',
  'American Bobtail',
  'American Curl',
  'American Shorthair',
  'American Wirehair',
  'Arabian Mau',
  'Australian Mist',
  'Balinese',
  'Bambino',
  'Bengal',
  'Birman',
  'Black footed cat',
  'Bombay',
  'Brazilian shorthair',
  'British Longhair',
  'British Shorthair',
  'Burmese',
  'Burmilla',
  'Calico',
  'California Spangled Cat',
  'Caracal',
  'Chantilly/Tiffany',
  'Chartreux',
  'Chausie',
  'Chinchilla',
  'Clouded Leopard',
  'Color point shorthair',
  'Community Cat',
  'Cornish Rex',
  'Cymric',
  'Devon Rex',
  'Domestic Cat',
  'Domestic Long Hair',
  'Domestic Medium Hair',
  'Domestic Short Hair',
  'Donskoy',
  'Dwarf',
  'Dwelf',
  'Egyptian Mau',
  'European',
  'Exotic Longhair',
  'Exotic Shorthair',
  'Foldex',
  'Havana Brown',
  'Highland Lynx',
  'Himalayan',
  'Japanese Bobtail',
  'Javanese',
  'Khao Manee',
  'Korat',
  'Kurilian Bobtail',
  'LaPerm',
  'Lykoi',
  'Lynx',
  'Maine Coon',
  'Manx',
  'Mix',
  'Moggie',
  'Munchkin',
  'Napoleon',
  'Nebelung',
  'Neva Masquerade',
  'Norwegian Forest cat',
  'Ocicat',
  'Oriental',
  'Persian',
  'Peterbald',
  'Pixie Bob',
  'Ragamuffin',
  'Ragdoll',
  'Russian Blue',
  'Savannah',
  'Scottish Fold',
  'Scottish Shorthair',
  'Scottish Straight',
  'Selkirk Rex',
  'Serval',
  'Siamese',
  'Siberian',
  'Singapura',
  'Skookum',
  'Snowshoe',
  'Somali',
  'Sphynx',
  'Tabby',
  'Tonkinese',
  'Toybob',
  'Turkish Angora',
  'Turkish Van',
  'Ukrainian Levkoy',
  'Unknown',
  'York Chocolate',
] as const
